import { Link, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { fetchCategories, fetchShopProducts } from '../api/catalog';

export function CategoryPage() {
  const { slug } = useParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError('');
      try {
        const [catResult, productResult] = await Promise.allSettled([
          fetchCategories(ac.signal),
          fetchShopProducts(ac.signal),
        ]);

        if (cancelled) return;

        if (catResult.status === 'fulfilled') {
          setCategories(Array.isArray(catResult.value) ? catResult.value : []);
        }
        if (productResult.status === 'fulfilled') {
          setProducts(Array.isArray(productResult.value?.products) ? productResult.value.products : []);
        }

        const errors = [];
        if (catResult.status === 'rejected' && catResult.reason?.name !== 'AbortError') {
          errors.push(catResult.reason instanceof Error ? catResult.reason.message : String(catResult.reason));
        }
        if (productResult.status === 'rejected' && productResult.reason?.name !== 'AbortError') {
          errors.push(productResult.reason instanceof Error ? productResult.reason.message : String(productResult.reason));
        }
        if (errors.length) {
          setError(errors.join(' · '));
        }
      } catch (e) {
        if (cancelled || e?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Unable to load category data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [slug]);

  const category = useMemo(
    () => categories.find((c) => String(c.slug || c.id) === String(slug)),
    [categories, slug],
  );

  const items = useMemo(() => {
    if (!category) return [];
    const categoryKey = String(category.slug || category.id);
    return products.filter((product) => {
      const productCategory =
        product.category_slug ?? product.category?.slug ?? product.category?.id ?? null;
      return String(productCategory) === categoryKey;
    });
  }, [category, products]);

  if (loading) {
    return (
      <main className="container" style={{ padding: '24px 16px 48px' }}>
        <p>Loading category…</p>
      </main>
    );
  }

  if (!category) {
    return (
      <main className="container" style={{ padding: '24px 16px 48px' }}>
        <h1 style={{ margin: 0 }}>Category not found</h1>
        {error ? <p style={{ color: 'var(--danger)' }}>{error}</p> : null}
        <p style={{ color: 'var(--muted)' }}>Try the shop page.</p>
        <Link className="btn" to="/shop">
          Back to Shop
        </Link>
      </main>
    );
  }

  return (
    <main className="container" style={{ padding: '24px 16px 48px' }}>
      <h1 style={{ margin: 0 }}>{category.name || category.slug || category.id}</h1>
      <p style={{ color: 'var(--muted)' }}>{items.length} products</p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 14,
          marginTop: 14,
        }}
      >
        {items.map((p) => (
          <Link
            key={p.id ?? p.slug ?? p.url}
            to={`/product/${p.slug || p.url?.split('/').filter(Boolean).pop()}`}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: 16,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{p.name}</div>
            <div style={{ color: 'var(--muted)' }}>
              {typeof p.priceText === 'string' ? p.priceText : `$${Number(p.price).toFixed(2)}`}
            </div>
          </Link>
        ))}
      </div>

      {error ? (
        <div style={{ marginTop: 20, color: 'var(--danger)' }}>
          Error loading category data: {error}
        </div>
      ) : null}
    </main>
  );
}

